/**
 * BlacklistIpBlockAnonymizerBlockPage — IP blacklist rule list + anonymizer
 * umbrella toggle + test-IP probe. GREEN-only accent. Light theme only.
 * No emerald, blue, orange, purple, dark, or emojis.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	BLACKLIST_KINDS,
	PROXY_CLASSES,
	labelForKind,
	simulateEvaluate,
	sourceForRule,
	type BlacklistKind,
	type BlacklistRule,
	type EvaluateBlacklistBody,
	type EvaluateBlacklistResponse,
	type ProxyClass,
} from "./api";
import {
	useBlacklistIpBlockAnonymizerBlockCreate,
	useBlacklistIpBlockAnonymizerBlockDelete,
	useBlacklistIpBlockAnonymizerBlockEvaluate,
	useBlacklistIpBlockAnonymizerBlockList,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-xs font-medium uppercase tracking-wide text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL =
	"inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[11px] font-medium";
const PILL_NEUTRAL = `${PILL} border-zinc-200 bg-zinc-50 text-zinc-700`;
const PILL_GREEN = `${PILL} border-green-200 bg-green-50 text-green-700`;
const PILL_MUTED = `${PILL} border-zinc-200 bg-white text-zinc-500`;

function formatCreatedAt(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "—";
	const d = new Date(sec * 1000);
	return d.toISOString().replace("T", " ").slice(0, 16) + "Z";
}

function defaultValueForKind(k: BlacklistKind): string {
	if (k === "tor") return "tor";
	if (k === "vpn") return "vpn";
	if (k === "anonymous_all") return "anonymous_all";
	return "";
}

export function BlacklistIpBlockAnonymizerBlockPage() {
	const listQuery = useBlacklistIpBlockAnonymizerBlockList();
	const createMut = useBlacklistIpBlockAnonymizerBlockCreate();
	const deleteMut = useBlacklistIpBlockAnonymizerBlockDelete();
	const evaluateMut = useBlacklistIpBlockAnonymizerBlockEvaluate();

	const [kind, setKind] = useState<BlacklistKind>("ip");
	const [value, setValue] = useState("");
	const [note, setNote] = useState("");

	const [probeIp, setProbeIp] = useState("");
	const [probeOffer, setProbeOffer] = useState("");
	const [probeAnon, setProbeAnon] = useState(false);
	const [probeTor, setProbeTor] = useState(false);
	const [probeVpn, setProbeVpn] = useState(false);
	const [probeProxy, setProbeProxy] = useState<"" | ProxyClass>("");
	const [probeResult, setProbeResult] = useState<
		EvaluateBlacklistResponse | null
	>(null);

	const rules: BlacklistRule[] = listQuery.data?.items ?? [];

	const umbrella = useMemo(
		() => rules.find((r) => r.kind === "anonymous_all") ?? null,
		[rules],
	);

	const handleKindChange = (next: BlacklistKind) => {
		setKind(next);
		setValue(defaultValueForKind(next));
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const v = value.trim();
		if (!v) return;
		createMut.mutate(
			{
				kind,
				value: v,
				note: note.trim() || null,
			},
			{
				onSuccess: () => {
					setValue(defaultValueForKind(kind));
					setNote("");
				},
			},
		);
	};

	const handleUmbrellaToggle = () => {
		if (umbrella) {
			deleteMut.mutate(umbrella.id);
			return;
		}
		createMut.mutate({
			kind: "anonymous_all",
			value: "anonymous_all",
			note: "umbrella toggle",
		});
	};

	const handleProbe = (e: FormEvent) => {
		e.preventDefault();
		const body: EvaluateBlacklistBody = {
			ip: probeIp.trim() || undefined,
			offer_id: probeOffer.trim() || null,
			flags: {
				anonymizer: probeAnon || undefined,
				tor: probeTor || undefined,
				vpn: probeVpn || undefined,
				proxy: probeProxy || undefined,
			},
		};
		// Client-side simulate for immediate feedback; also call BE.
		setProbeResult(simulateEvaluate(rules, body));
		evaluateMut.mutate(body, {
			onSuccess: (res) => setProbeResult(res),
		});
	};

	return (
		<main
			data-testid="blacklist-ip-block-anonymizer-block-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Traffic shielding
				</p>
				<h1 className="text-3xl font-semibold text-zinc-900">
					IP blacklist &amp; anonymizer block
				</h1>
				<p className="text-sm text-zinc-600">
					Block clicks by IP, CIDR range, or anonymizer signal (Tor, VPN,
					proxy). Rules apply per tenant; offer-scoped rules override the
					umbrella when present.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Anonymizer umbrella toggle"
				data-testid="blacklist-ip-block-anonymizer-block-umbrella"
			>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h2 className="text-base font-semibold text-zinc-900">
							Anonymizer umbrella
						</h2>
						<p className="mt-1 text-sm text-zinc-600">
							Blocks any click flagged as Tor, VPN, proxy, or generic
							anonymizer — regardless of specific rule matches.
						</p>
					</div>
					<div className="flex items-center gap-3">
						<span
							className={umbrella ? PILL_GREEN : PILL_MUTED}
							data-testid="blacklist-ip-block-anonymizer-block-umbrella-state"
						>
							{umbrella ? "Enabled" : "Disabled"}
						</span>
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={handleUmbrellaToggle}
							disabled={createMut.isPending || deleteMut.isPending}
							data-testid="blacklist-ip-block-anonymizer-block-umbrella-toggle"
						>
							{umbrella ? "Disable" : "Enable"}
						</button>
					</div>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Create blacklist rule"
				data-testid="blacklist-ip-block-anonymizer-block-create-card"
			>
				<h2 className="text-base font-semibold text-zinc-900">Add rule</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr_auto]"
					data-testid="blacklist-ip-block-anonymizer-block-create-form"
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="bl-kind" className={LABEL}>
							Kind
						</label>
						<select
							id="bl-kind"
							className={SELECT}
							value={kind}
							onChange={(e) =>
								handleKindChange(e.currentTarget.value as BlacklistKind)
							}
							data-testid="blacklist-ip-block-anonymizer-block-kind-select"
						>
							{BLACKLIST_KINDS.map((k) => (
								<option key={k} value={k}>
									{labelForKind(k)}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="bl-value" className={LABEL}>
							Value
						</label>
						{kind === "proxy" ? (
							<select
								id="bl-value"
								className={SELECT}
								value={value}
								onChange={(e) => setValue(e.currentTarget.value)}
								data-testid="blacklist-ip-block-anonymizer-block-value-input"
							>
								<option value="">Select proxy class…</option>
								{PROXY_CLASSES.map((p) => (
									<option key={p} value={p}>
										{p}
									</option>
								))}
							</select>
						) : (
							<input
								id="bl-value"
								type="text"
								className={INPUT}
								placeholder={
									kind === "ip"
										? "e.g. 203.0.113.7"
										: kind === "cidr"
											? "e.g. 203.0.113.0/24"
											: kind
								}
								value={value}
								onChange={(e) => setValue(e.currentTarget.value)}
								disabled={createMut.isPending}
								readOnly={
									kind === "tor" || kind === "vpn" || kind === "anonymous_all"
								}
								data-testid="blacklist-ip-block-anonymizer-block-value-input"
							/>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<span className={LABEL}>&nbsp;</span>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMut.isPending || !value.trim()}
							data-testid="blacklist-ip-block-anonymizer-block-submit"
						>
							{createMut.isPending ? "Saving" : "Add rule"}
						</button>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-3">
						<label htmlFor="bl-note" className={LABEL}>
							Note (optional)
						</label>
						<input
							id="bl-note"
							type="text"
							className={INPUT}
							placeholder="Context / reason"
							value={note}
							onChange={(e) => setNote(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="blacklist-ip-block-anonymizer-block-note-input"
						/>
					</div>
				</form>
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="blacklist-ip-block-anonymizer-block-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Test IP probe"
				data-testid="blacklist-ip-block-anonymizer-block-probe-card"
			>
				<h2 className="text-base font-semibold text-zinc-900">Test IP probe</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Simulate an inbound click against the current rule set.
				</p>
				<form
					onSubmit={handleProbe}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="blacklist-ip-block-anonymizer-block-probe-form"
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="probe-ip" className={LABEL}>
							IP
						</label>
						<input
							id="probe-ip"
							type="text"
							className={INPUT}
							placeholder="203.0.113.7"
							value={probeIp}
							onChange={(e) => setProbeIp(e.currentTarget.value)}
							data-testid="blacklist-ip-block-anonymizer-block-probe-ip"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="probe-offer" className={LABEL}>
							Offer id (optional)
						</label>
						<input
							id="probe-offer"
							type="text"
							className={INPUT}
							placeholder="offer_123"
							value={probeOffer}
							onChange={(e) => setProbeOffer(e.currentTarget.value)}
							data-testid="blacklist-ip-block-anonymizer-block-probe-offer"
						/>
					</div>
					<div className="flex flex-col gap-2 sm:col-span-2">
						<span className={LABEL}>Flags</span>
						<div className="flex flex-wrap gap-4 text-sm text-zinc-700">
							<label className="inline-flex items-center gap-2">
								<input
									type="checkbox"
									className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600/30"
									checked={probeAnon}
									onChange={(e) => setProbeAnon(e.currentTarget.checked)}
									data-testid="blacklist-ip-block-anonymizer-block-probe-anon"
								/>
								Anonymizer
							</label>
							<label className="inline-flex items-center gap-2">
								<input
									type="checkbox"
									className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600/30"
									checked={probeTor}
									onChange={(e) => setProbeTor(e.currentTarget.checked)}
									data-testid="blacklist-ip-block-anonymizer-block-probe-tor"
								/>
								Tor
							</label>
							<label className="inline-flex items-center gap-2">
								<input
									type="checkbox"
									className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600/30"
									checked={probeVpn}
									onChange={(e) => setProbeVpn(e.currentTarget.checked)}
									data-testid="blacklist-ip-block-anonymizer-block-probe-vpn"
								/>
								VPN
							</label>
							<label className="inline-flex items-center gap-2">
								<span className="text-xs text-zinc-500">Proxy</span>
								<select
									className={SELECT}
									value={probeProxy}
									onChange={(e) =>
										setProbeProxy(e.currentTarget.value as "" | ProxyClass)
									}
									data-testid="blacklist-ip-block-anonymizer-block-probe-proxy"
								>
									<option value="">none</option>
									{PROXY_CLASSES.map((p) => (
										<option key={p} value={p}>
											{p}
										</option>
									))}
								</select>
							</label>
						</div>
					</div>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={evaluateMut.isPending}
							data-testid="blacklist-ip-block-anonymizer-block-probe-submit"
						>
							{evaluateMut.isPending ? "Probing" : "Run probe"}
						</button>
					</div>
				</form>
				{probeResult ? (
					<div
						className="mt-3 rounded-[6px] border border-zinc-200 bg-white p-3 text-sm"
						data-testid="blacklist-ip-block-anonymizer-block-probe-result"
					>
						{probeResult.blocked ? (
							<div className="flex flex-col gap-1">
								<span
									className={PILL_GREEN}
									data-testid="blacklist-ip-block-anonymizer-block-probe-blocked"
								>
									Blocked
								</span>
								{probeResult.rule ? (
									<span className="text-xs text-zinc-600">
										Matched {labelForKind(probeResult.rule.kind)} ·{" "}
										<span className="font-mono">{probeResult.rule.value}</span>
									</span>
								) : null}
							</div>
						) : (
							<span
								className={PILL_NEUTRAL}
								data-testid="blacklist-ip-block-anonymizer-block-probe-allowed"
							>
								Allowed
							</span>
						)}
					</div>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Blacklist rules"
				data-testid="blacklist-ip-block-anonymizer-block-list-card"
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Rules</h2>
					<span className="text-xs text-zinc-500">
						{rules.length === 0 ? "0 rules" : `${rules.length} rule(s)`}
					</span>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="blacklist-ip-block-anonymizer-block-loading"
					>
						Loading rules
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="blacklist-ip-block-anonymizer-block-error"
					>
						{listQuery.error.message}
					</p>
				) : rules.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="blacklist-ip-block-anonymizer-block-empty"
					>
						No rules yet. Add an IP, CIDR, or enable the anonymizer umbrella
						above.
					</p>
				) : (
					<div
						className="mt-3 overflow-x-auto"
						data-testid="blacklist-ip-block-anonymizer-block-list"
					>
						<table className="min-w-full text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-3 font-medium">Kind</th>
									<th className="py-2 pr-3 font-medium">Value</th>
									<th className="py-2 pr-3 font-medium">Scope</th>
									<th className="py-2 pr-3 font-medium">Source</th>
									<th className="py-2 pr-3 font-medium">Added</th>
									<th className="py-2 pr-3 font-medium">Note</th>
									<th className="py-2 pr-3 font-medium">Enabled</th>
									<th className="py-2" />
								</tr>
							</thead>
							<tbody>
								{rules.map((r) => (
									<tr
										key={r.id}
										className="border-b border-zinc-100"
										data-testid={`blacklist-ip-block-anonymizer-block-row-${r.id}`}
									>
										<td className="py-2 pr-3">
											<span className={PILL_NEUTRAL}>{labelForKind(r.kind)}</span>
										</td>
										<td className="py-2 pr-3 font-mono text-xs text-zinc-800">
											{r.value}
										</td>
										<td className="py-2 pr-3 text-xs text-zinc-600">
											{r.offer_id ? (
												<span className="font-mono">{r.offer_id}</span>
											) : (
												<span className="text-zinc-400">global</span>
											)}
										</td>
										<td className="py-2 pr-3">
											<span className={PILL_NEUTRAL}>{sourceForRule(r)}</span>
										</td>
										<td className="py-2 pr-3 text-xs text-zinc-600">
											{formatCreatedAt(r.created_at)}
										</td>
										<td className="py-2 pr-3 text-xs text-zinc-600">
											{r.note ? (
												<span>{r.note}</span>
											) : (
												<span className="text-zinc-400">—</span>
											)}
										</td>
										<td className="py-2 pr-3">
											<span className={PILL_GREEN}>on</span>
										</td>
										<td className="py-2">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => deleteMut.mutate(r.id)}
												disabled={deleteMut.isPending}
												data-testid={`blacklist-ip-block-anonymizer-block-delete-${r.id}`}
											>
												Delete
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default BlacklistIpBlockAnonymizerBlockPage;
